/**
 * RichText — safe HTML renderer for content produced by RichTextEditor.
 *
 * - If the value already contains HTML tags → sanitize + render as HTML.
 * - Otherwise → treat as legacy plain text: split by blank lines into
 *   paragraphs and preserve intra-paragraph newlines, mirroring the
 *   `whitespace-pre-line` convention used across BriteHub. URLs stay clickable
 *   via the existing Linkify helper.
 */
import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import Linkify from '@/lib/linkify';

const HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;
const HTML_ENTITY_RE = /&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);/;

/**
 * Decode HTML entities (e.g. `&#8217;`, `&amp;`) inside plain-text values.
 * Uses the DOM parser so we get browser-accurate mappings without a big
 * lookup table.
 */
function decodeEntities(text) {
  if (typeof window === 'undefined' || !text) return text;
  if (!HTML_ENTITY_RE.test(text)) return text;
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
    'blockquote', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4',
    'a', 'span', 'mark', 'hr',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'data-color', 'data-mention-id', 'data-type', 'data-id', 'data-label'],
  ALLOW_DATA_ATTR: true,
};

/**
 * Renders rich-text or legacy plain-text content.
 *
 * @param {string} html    raw string from the backend
 * @param {string} className tailwind classes for the wrapper
 * @param {string} testId  data-testid on the wrapper (optional)
 */
export default function RichText({ html, className = '', testId }) {
  const value = (html || '').trim();
  const rootRef = useRef(null);
  const navigate = useNavigate();

  // Intercept clicks on internal anchor tags produced by the editor — mention
  // chips (`/profile/public/*`) and other same-origin links — so they navigate
  // through React Router instead of triggering a full page reload.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onClick = (ev) => {
      const a = ev.target.closest && ev.target.closest('a[href]');
      if (!a || !el.contains(a)) return;
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('/')) return;
      if (a.target && a.target !== '' && a.target !== '_self') return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      ev.preventDefault();
      navigate(href);
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [navigate]);

  if (!value) return null;

  if (HTML_TAG_RE.test(value)) {
    const clean = DOMPurify.sanitize(value, SANITIZE_CONFIG);
    return (
      <div
        ref={rootRef}
        className={`rich-text ${className}`}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  // Legacy plain text — split double newlines into paragraphs.
  const decoded = decodeEntities(value);
  const paragraphs = decoded.split(/\n\s*\n+/).map(p => p.trim()).filter(Boolean);
  return (
    <div ref={rootRef} className={`rich-text ${className}`} data-testid={testId}>
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          <Linkify text={p} />
        </p>
      ))}
    </div>
  );
}

/**
 * True if the given string appears to contain any HTML markup — useful when a
 * consumer wants to short-circuit their own rendering path.
 */
export const isRichHtml = (value) => !!value && HTML_TAG_RE.test(String(value));
